import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "@/atoms/alert";
import { Button } from "@/atoms/button";
import { Modal } from "@/components/modal";
import { formatLongDate } from "@/utils/date";
import { cadenceLine } from "@/utils/interval";
import { formatRunningMoney, fromCents } from "@/utils/money";
import type { PortalSubscription } from "@/data/portal";

/**
 * The one confirmation in this app (spec modal 1.7 + button-destructive 1.5).
 *
 * The title asks the question with the product in it, the subtitle keeps the money and the date
 * in view while the body is read, and the body says exactly what the client keeps and for how
 * long. The safe action is first and takes focus; the destructive one is last and is the only
 * solid semantic fill in the whole system.
 *
 * The dialog stays open until the server has answered: closing it before the result is known
 * would be reporting an action that has not happened (button-destructive 1.5 doNot 6). A failure
 * is an alert inside the body and the dialog stays where it is.
 */

export interface CancelModalProps {
  subscription: PortalSubscription | null;
  coachName: string;
  cancelling: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (subscription: PortalSubscription) => void;
}

export function CancelModal({
  subscription,
  coachName,
  cancelling,
  error,
  onClose,
  onConfirm,
}: CancelModalProps) {
  const { t } = useTranslation();
  /* The dialog is not unmounted the instant the subscription goes: `.ds-rise` needs its closed
     frame to play, and a component that returns null on close never gets one. The last
     subscription is held for exactly that frame, so the words do not blank out while the sheet
     is still on screen. */
  const lastRef = useRef<PortalSubscription | null>(null);
  if (subscription) lastRef.current = subscription;
  const shown = subscription ?? lastRef.current;
  if (!shown) return null;

  const price = shown.price;
  const amount = formatRunningMoney(fromCents(price.amount), price.currency);
  const cadence = price.interval
    ? cadenceLine(t, {
        interval: price.interval,
        intervalCount: price.intervalCount ?? 1,
      })
    : "";
  const until = formatLongDate(shown.currentPeriodEnd) ?? "";

  return (
    <Modal
      open={!!subscription}
      onClose={cancelling ? () => undefined : onClose}
      title={t("gymkeePay.portal.cancelTitle", {
        product: shown.productName,
      })}
      subtitle={t("gymkeePay.portal.cancelSubtitle", {
        amount,
        cadence,
        date: until,
      })}
      footer={
        <>
          <Button
            variant="ghost"
            className="ds-pay-hand"
            onClick={onClose}
            disabled={cancelling}
          >
            {t("gymkeePay.portal.keepMySubscription")}
          </Button>
          <Button
            variant="destructive"
            className="ds-pay-hand"
            loading={cancelling}
            onClick={() => onConfirm(shown)}
          >
            {t("gymkeePay.portal.cancelConfirm")}
          </Button>
        </>
      }
    >
      <p>
        {t("gymkeePay.portal.cancelBody", { date: until, coach: coachName })}
      </p>
      {error && <Alert tone="danger">{error}</Alert>}
    </Modal>
  );
}
